import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Spin,
  Alert,
  Button,
  Typography,
  Tooltip,
  Space,
  Popconfirm,
  message,
} from "antd";
import apiClient from 'services/apiClient';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelFilled,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { sortBy } from "lodash";

const { Title } = Typography;

const CleanUpHistory = ({ api, authToken, updateState }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch cleanup records when component mounts
    fetchCleanupRecords();
  }, [updateState]);

  const fetchCleanupRecords = async () => {
    try {
      setLoading(true);
      const response = await apiClient({
        method: "get",
        url: `/cleanup/records`,
      });

      if (response.data && response.data.data) {
        setRecords(response.data.data);
      } else {
        setRecords([]);
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch cleanup records:", err);
      setError("Failed to load cleanup history. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl);
  };
  const handleDeleteRecord = async (recordId) => {
    try {
      setLoading(true);
      const response = await apiClient({
        method: "post",
        url: `/cleanup/delete-cleanup-records`,
        data: { record_id: recordId },
      });

      if (response.data && response.data.success) {
        message.success("Record deleted successfully");
        fetchCleanupRecords(); // Refresh the data
      } else {
        message.error(response.data?.message || "Failed to delete record");
      }
    } catch (err) {
      console.error("Failed to delete record:", err);
      message.error(
        err.response?.data?.message ||
          "Failed to delete record. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => moment(text).format("YYYY-MM-DD HH:mm"),
      sorter: (a, b) =>
        moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: "Data",
      dataIndex: "table_name",
      key: "table_name",
      render: (text) => {
        let color = "blue";
        let displayText = text;

        // Handle the four specific table types
        if (text === "report" || text === "reports") {
          color = "green";
          displayText = "Incoming Reports";
        } else if (
          text === "out_reports" ||
          text.toLowerCase() === "outreport"
        ) {
          color = "purple";
          displayText = "Outgoing Reports";
        } else if (text === "campaign") {
          color = "orange";
          displayText = "Campaigns";
        } else if (text === "campaign_reports") {
          color = "red";
          displayText = "Campaign Reports";
        }

        return <Tag color={color}>{displayText}</Tag>;
      },
      filters: [
        { text: "Incoming Reports", value: "report" },
        { text: "Outgoing Reports", value: "outReport" },
        { text: "Campaigns", value: "campaign" },
        { text: "Campaign Reports", value: "campaign_reports" },
      ],
      onFilter: (value, record) =>
        record.table_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Operation By",
      dataIndex: null,
      render: (row) => {
        return row?.action_by?.name;
      },
    },
    {
      title: "Action Type",
      dataIndex: "action",
      key: "action",
      render: (text) => {
        const color = text === "export" ? "blue" : "red";
        return (
          <Tag color={color}>
            {text === "export" ? (
              <>
                <FileExcelFilled style={{ marginRight: 4 }} /> EXPORT
              </>
            ) : (
              <>
                <DeleteOutlined style={{ marginRight: 4 }} /> DELETE
              </>
            )}
          </Tag>
        );
      },
      filters: [
        { text: "Export", value: "export" },
        { text: "Delete", value: "delete" },
      ],
      onFilter: (value, record) => record.action_type === value,
    },
    {
      title: "User",
      dataIndex: "action_by_name",
      key: "action_by_name",
      render: (text) => {
        return text ?? "All";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.link ? (
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.link)}
            />
          ) : (
            <Tooltip title="No file available">
              <Button size="small" icon={<FileTextOutlined />} disabled />
            </Tooltip>
          )}

          <Popconfirm
            title="Delete this record"
            description="Are you sure you want to delete this record?"
            onConfirm={() => handleDeleteRecord(record.id)}
            onCancel={() => setLoading(false)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={loading}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center" style={{marginBottom: "16px"}}>
        <Title level={4} style={{ margin: 0 }}>
          Clean Up History
        </Title>
        <Button
          onClick={fetchCleanupRecords}
          loading={loading}
          size="small"
          style={{ lineHeight: 0 }}
        >
          <ReloadOutlined />
        </Button>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={records}
          rowKey={(record) =>
            record.id || Math.random().toString(36).substring(2)
          }
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          scroll={{ x: "max-content" }}
        />
      </Spin>
    </div>
  );
};

export default CleanUpHistory;
